<script setup lang="ts">
/**
 * KbPicker — chip-row above the chat composer (ADR 0019 §UI).
 *
 * Lets the caller select 0..N of their accessible KBs for the current
 * conversation. v1 multi-select (the search_personal_kb tool already
 * intersects whatever's selected); flip to single-select by capping
 * the array if product wants tighter scope later.
 *
 * Lifecycle:
 *   - mount  → fetch /api/kbs; mark which are "selected" by matching
 *              against `modelValue` (the conversation.kb_ids).
 *   - toggle → optimistic local update + PATCH the conversation.
 *   - if a referenced id no longer maps to an accessible KB (the KB
 *     was deleted between turns, or a share was revoked) we show a
 *     muted "1 KB unavailable" badge per ADR 0019 §UI.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

import { listKbs, setConversationKbIds, type Kb } from "../api/kbs";

const props = defineProps<{
  /** OID/email of the dev or signed-in user — auth-header helper input. */
  devUser: string;
  /** Conversation we're scoping selection to. null = no chat yet: the
   *  picker still shows and is selectable; the selection lives in local
   *  state and is carried on the first chat request (which scopes the first
   *  answer and persists kb_ids onto the conversation the backend creates).
   *  Once non-null, toggles persist immediately via PATCH. */
  conversationId: number | null;
  /** Current selection (the conversation.kb_ids field). */
  modelValue: number[];
}>();

const emit = defineEmits<{
  (e: "update:modelValue", v: number[]): void;
  (e: "error", msg: string): void;
}>();

const kbs = ref<Kb[]>([]);
const loading = ref(false);
const expanded = ref(false);
/** Root element — used to detect clicks/taps outside the picker so the
 *  open menu dismisses like a normal popover (clicking the composer or
 *  anywhere else closes it), not only when the toggle is re-clicked. */
const rootEl = ref<HTMLElement | null>(null);

const accessibleIds = computed(() => new Set(kbs.value.map(k => k.id)));

const selected = computed<Set<number>>(() => new Set(props.modelValue));

/** Ids the conversation references but the API didn't return — KB
 *  was deleted or share revoked. Surface a "N unavailable" badge so
 *  the user can prune the selection rather than wondering why nothing
 *  matches in chat. */
const orphanedCount = computed(() => {
  let n = 0;
  for (const id of props.modelValue) {
    if (!accessibleIds.value.has(id)) n += 1;
  }
  return n;
});

const selectedList = computed(() =>
  kbs.value.filter(k => selected.value.has(k.id)),
);

async function load() {
  loading.value = true;
  try {
    kbs.value = await listKbs(props.devUser);
  } catch (e) {
    emit("error", `Could not load your KBs: ${(e as Error).message}`);
  } finally {
    loading.value = false;
  }
}

// Dismiss the open menu on any pointer-down outside the picker, or Escape.
function onDocPointerDown(e: MouseEvent) {
  if (!expanded.value) return;
  const root = rootEl.value;
  if (root && e.target instanceof Node && !root.contains(e.target)) {
    expanded.value = false;
  }
}
function onKeydown(e: KeyboardEvent) {
  if (e.key === "Escape" && expanded.value) expanded.value = false;
}

onMounted(() => {
  void load();
  document.addEventListener("mousedown", onDocPointerDown);
  document.addEventListener("keydown", onKeydown);
});
onBeforeUnmount(() => {
  document.removeEventListener("mousedown", onDocPointerDown);
  document.removeEventListener("keydown", onKeydown);
});

// Re-fetch when the dev-user changes (the simulator swap in local dev).
watch(() => props.devUser, () => { void load(); });

async function toggle(id: number) {
  const next = new Set(selected.value);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  const arr = Array.from(next);
  emit("update:modelValue", arr);
  // No conversation yet (KB picked before the first message): keep the
  // selection in local state only — the chat request carries kb_ids for the
  // first turn, and the backend persists them onto the conversation it
  // creates. Once a conversation exists, persist immediately so later turns
  // and a page reload reflect it.
  if (props.conversationId === null) return;
  try {
    await setConversationKbIds(props.devUser, props.conversationId, arr);
  } catch (e) {
    // Rollback on failure so the chip state matches the server.
    emit("update:modelValue", props.modelValue);
    emit("error", `Saving KB selection failed: ${(e as Error).message}`);
  }
}

async function clearAll() {
  emit("update:modelValue", []);
  if (props.conversationId === null) return;
  try {
    await setConversationKbIds(props.devUser, props.conversationId, []);
  } catch (e) {
    emit("update:modelValue", props.modelValue);
    emit("error", `Clearing KB selection failed: ${(e as Error).message}`);
  }
}

const buttonLabel = computed(() => {
  if (loading.value) return "Loading KBs…";
  if (kbs.value.length === 0) return "Knowledge bases · none yet";
  const n = selected.value.size;
  if (n === 0) return `Knowledge bases · ${kbs.value.length} available`;
  if (n === 1) return `Knowledge base · ${selectedList.value[0]?.name ?? "1 selected"}`;
  return `Knowledge bases · ${n} selected`;
});
</script>

<template>
  <div ref="rootEl" class="kb-picker">
    <button
      type="button"
      class="kb-picker__toggle"
      :class="{ 'kb-picker__toggle--active': selected.size > 0 }"
      @click="expanded = !expanded"
    >
      <span class="kb-picker__icon" aria-hidden="true">📚</span>
      <span>{{ buttonLabel }}</span>
      <span
        v-if="orphanedCount > 0"
        class="kb-picker__orphan"
        :title="
          orphanedCount === 1
            ? '1 KB unavailable (deleted or share revoked)'
            : `${orphanedCount} KBs unavailable (deleted or share revoked)`
        "
      >
        ⚠ {{ orphanedCount }} unavailable
      </span>
      <span class="kb-picker__chev" aria-hidden="true">
        {{ expanded ? "▴" : "▾" }}
      </span>
    </button>

    <div v-if="expanded" class="kb-picker__menu" role="listbox">
      <p v-if="!loading && kbs.length === 0" class="kb-picker__empty">
        No knowledge bases yet.
        <a href="#/kbs">Create one</a> to ground chats in your own files.
      </p>

      <ul v-else class="kb-picker__list">
        <li v-for="kb in kbs" :key="kb.id">
          <label class="kb-picker__row">
            <input
              type="checkbox"
              :checked="selected.has(kb.id)"
              @change="toggle(kb.id)"
            />
            <span class="kb-picker__name">{{ kb.name }}</span>
            <span class="kb-picker__count">
              {{ kb.doc_count }} {{ kb.doc_count === 1 ? "doc" : "docs" }}
            </span>
          </label>
        </li>
      </ul>

      <div v-if="selected.size > 0" class="kb-picker__actions">
        <button
          type="button"
          class="kb-picker__clear"
          @click="clearAll"
        >
          Clear selection
        </button>
        <a class="kb-picker__manage" href="#/kbs">Manage KBs →</a>
      </div>
      <div v-else class="kb-picker__actions">
        <a class="kb-picker__manage" href="#/kbs">Manage KBs →</a>
      </div>
    </div>
  </div>
</template>

<style scoped>
.kb-picker {
  position: relative;
  margin-bottom: var(--space-2);
}
.kb-picker__toggle {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: 0.35rem 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: 999px;
  background: var(--color-surface);
  color: var(--color-ink);
  font-size: 0.85rem;
  cursor: pointer;
}
.kb-picker__toggle:hover { border-color: var(--color-accent); }
.kb-picker__toggle--active {
  border-color: var(--color-accent);
  color: var(--color-accent);
  background: color-mix(in srgb, var(--color-accent) 6%, var(--color-surface));
}
.kb-picker__icon { font-size: 0.9rem; }
.kb-picker__chev { font-size: 0.7rem; opacity: 0.6; }
.kb-picker__orphan {
  font-size: 0.75rem;
  color: var(--color-muted);
  padding-left: var(--space-2);
  border-left: 1px solid var(--color-border);
  margin-left: var(--space-1);
}
.kb-picker__menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 20;
  min-width: 320px;
  max-height: 320px;
  overflow-y: auto;
  padding: var(--space-3);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);
}
.kb-picker__empty { color: var(--color-muted); font-size: 0.85rem; margin: 0; }
.kb-picker__list { list-style: none; padding: 0; margin: 0; }
.kb-picker__row {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: 0.3rem 0;
  cursor: pointer;
}
.kb-picker__row:hover { background: color-mix(in srgb, var(--color-accent) 4%, transparent); }
.kb-picker__name { flex: 1; }
.kb-picker__count { color: var(--color-muted); font-size: 0.8rem; }
.kb-picker__actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: var(--space-2);
  padding-top: var(--space-2);
  border-top: 1px solid var(--color-border);
  font-size: 0.85rem;
}
.kb-picker__clear {
  border: none;
  background: none;
  color: var(--color-muted);
  cursor: pointer;
  padding: 0;
}
.kb-picker__clear:hover { color: var(--color-ink); text-decoration: underline; }
.kb-picker__manage { color: var(--color-accent); text-decoration: none; }
.kb-picker__manage:hover { text-decoration: underline; }
</style>
